/* eslint-disable lumapps/no-classname-strings */
import React, { CSSProperties } from 'react';

import { classnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { useResponsive } from '@lumapps/responsive';
import { getCustomClasses } from '@lumapps/utils/string/getCustomClasses';

import { useComputeStyles } from '../../hooks/useComputeStyles';
import { useStickyCell } from '../../hooks/useStickyCell';
import * as types from '../../types';

import './index.scss';

type Props = Pick<types.Cell, 'properties' | 'width' | 'children'> & {
    path: string;
    style?: CSSProperties;
};

export const InnerCell = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
    const { properties, width, style, path, children } = props;

    const { get: getDataAttributes } = useDataAttributes('layout');
    const wrapperStyle = useComputeStyles(properties?.style?.components, true);

    const className = classnames('cell', getCustomClasses(properties?.class, 'component-cell--'), {
        'cell--is-sticky': properties?.sticky,
        'cell--is-plain': properties?.plain,
        [`cell--w-${width}`]: width,
    });

    return (
        <div
            {...getDataAttributes({ element: path })}
            ref={ref}
            id={properties?.id}
            className={className}
            // Inline style (used to inject sticky styles)
            style={style}
        >
            <div className="cell__wrapper" style={wrapperStyle}>
                {children}
            </div>
        </div>
    );
});
InnerCell.displayName = 'InnerCell';

/**
 * Wrap cell to implement sticky via a dedicated hook.
 */
export const Cell: React.FC<Props> = (props) => {
    const { isMobile } = useResponsive();
    const enable = props.properties?.sticky && !isMobile;

    return useStickyCell({ enable, props, component: InnerCell });
};
Cell.displayName = 'Cell';
